import { Fragment, FunctionComponent, useEffect } from 'react';
import * as React from 'react';

import { AnalyticsStore, RouteStore } from 'bernie-core';

import { AdvertisingStore } from 'stores/advertising-store';
import { SearchResultsStore } from 'stores/search-results-store';

import { CarouselModel, HotelViewModel, InsightsViewModel } from 'models';
import { ResultType } from 'constants/resultType';

import { ListTitle } from './listing/list-title';
import { MesoInlineAdComponent, shouldMesoInlineAdRender } from 'components/meso/meso-inline-ad';
import { MesoNativeResultListing } from 'components/meso/meso-native-results-listing';
import { MessagingCardProps } from 'components/common/messaging-card';
import { RegionCompressionCard } from 'components/common/region-compression-card';
import { Result } from './result';
import * as queue from 'components/common/media/image-queue';

const LOADING_LIST = new Array(6).fill({});
const LAZY_LOADING_IMAGE_INDEX = 3;
const REGION_COMPRESSION_INDEX = 3;
const NRL_POS = 11;
const KEY_PREFIX = 'resultListing';
const MESSAGE_KEY_PREFIX = 'resultMessageListing';

export interface ResultsProps {
  hotels: HotelViewModel[];
  loading: boolean;
  startDate: string;
  endDate: string;
  insights?: InsightsViewModel;
  setResultRef: (ref: HTMLElement, index: number) => void;
  className?: string;
  messages: MessagingCardProps[];
  searchResults: SearchResultsStore;
  routeStore?: RouteStore;
  analytics?: AnalyticsStore;
  advertising?: AdvertisingStore;
}

export const Results: FunctionComponent<ResultsProps> = ({
  analytics,
  className,
  endDate,
  hotels,
  messages,
  insights,
  loading,
  routeStore,
  searchResults,
  setResultRef,
  startDate,
  advertising
}) => {
  useEffect(() => () => queue.dequeueAll('HSR'));

  const compression = insights && insights.compression;
  const iconBasedCompression = insights && insights.iconBasedCompression;
  const showCompressionMessaging = compression || iconBasedCompression;
  const showLoadingList = loading && (!hotels || hotels.length === 0);
  const classNames = `listing ${className}`;
  const carouselListings: CarouselModel[] = searchResults && searchResults.carouselListings ? searchResults.carouselListings : [];
  const resultSectionHeadings = searchResults && searchResults.resultSectionHeadings ? searchResults.resultSectionHeadings : [];

  const resultSectionHeadingsMap = resultSectionHeadings.reduce((acc, resultSectionHeading) => {
    acc[resultSectionHeading.index] = resultSectionHeading.heading;

    return acc;
  }, {});

  const messageMap = messages.reduce((obj, singleMessage) => {
    obj[singleMessage.index] = singleMessage;

    return obj;
  }, {});

  carouselListings.forEach(carouselListing => {
    if (carouselListing && carouselListing.model) {
      messageMap[carouselListing.index] = carouselListing;
    }
  });
  const truncatedHotelListings = showLoadingList ? LOADING_LIST : hotels.slice(0, searchResults.hotelsMaxDisplay);

  return (
    <section className="results" data-stid="section-results">
      <ol className="uitk-grid all-x-gutter-three all-y-gutter-three results-list">
        {truncatedHotelListings.map((listing, index) => {
          const resultProps = {
            className: classNames,
            endDate,
            startDate,
            index,
            lazyLoadImg: index >= LAZY_LOADING_IMAGE_INDEX,
            loading: showLoadingList,
            setResultRef,
            routeStore,
            analytics
          };

          return (
            <Fragment key={`${KEY_PREFIX}-${index}`}>
              {index === NRL_POS && <MesoNativeResultListing className={className} uci="NRL" errorBoundaryLabel="hotel.search.meso" />}

              {showCompressionMessaging && index === REGION_COMPRESSION_INDEX && (
                <li className={classNames}>
                  <RegionCompressionCard
                    key="region-compression-card"
                    analytics={analytics}
                    compression={compression}
                    iconBasedCompression={iconBasedCompression}
                  />
                </li>
              )}
              {shouldMesoInlineAdRender(advertising.triggeredPageName, index) && (
                <MesoInlineAdComponent
                  key={`mesoInlineAd${index}`}
                  slotIndex={index}
                  classNames={classNames}
                  pageId={advertising.triggeredPageName}
                />
              )}

              {messageMap[index] && (
                <Result
                  {...resultProps}
                  // we need the unique combination since sponsored listings can present a duplicate hotelId in the same list
                  key={`${MESSAGE_KEY_PREFIX}-${index}`}
                  model={messageMap[index].model}
                  resultType={messageMap[index].resultType ? messageMap[index].resultType : ResultType.MESSAGING}
                  stidKey={messageMap[index].stidKey}
                  type={messageMap[index].type}
                  lazyLoad={messageMap[index].lazyLoad}
                />
              )}

              {resultSectionHeadingsMap[index] && <ListTitle title={resultSectionHeadingsMap[index]} />}

              <Result
                {...resultProps}
                // we need the unique combination since sponsored listings can present a duplicate hotelId in the same list
                key={listing.id ? `${KEY_PREFIX}-${listing.id}-${index}` : `${KEY_PREFIX}-${index}`}
                model={listing.model || listing} // MessagingCard | Listing
                resultType={listing.resultType}
              />
            </Fragment>
          );
        })}
      </ol>
    </section>
  );
};
